import { useMemo, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraGridItem,
  ChakraSimpleGrid,
  ChakraSpacer,
  DateFormat,
  FAIconName,
  FireflyButton,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import ResendInvitation from '~/components/common/ResendInvitation';
import SupportLinkedAccountsModal from '~/components/common/SupportLinkedAccountsModal';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';

import PatientAddContactV2 from '../contactV2/PatientContactV2';
import {
  getContactPrimaryDetails,
  getContactSecondaryDetails,
} from '~/lib/util/patient.util';
import { renderField } from '../basic-info/ViewBasicInfo';
import DeleteConfirmModal from '~/components/common/user-profile/DeleteConfirmModal';
import { useDeletePatientContactMutation } from '~/store/services/patients';

interface PatientContactProps {
  contact: PatientContact;
  onAdd: () => void;
  patientId: string;
  patientBasicDetails: Patient;
  supportEmailAddresses: string[];
}

const TOP_LEVEL_ID = 'patient-contact-details';

const ViewPatientContact = ({ contact }: { contact: PatientContact }) => {
  const {
    address,
    cellPhone,
    chosenName,
    contactPreferences,
    dateOfBirth,
    department,
    email,
    ethnicities,
    firstName,
    gender,
    hasSetPassword,
    isLivesWithPatient,
    languages,
    lastName,
    middleName,
    officePhone,
    prefix,
    pronouns,
    relationToPatient,
    suffix,
    supportExternalId,
    timezone,
    isSupport,
    isProxy,
    otherLinkedPatients,
    secondaryCellPhone,
    faxNumber,
    homePhone,
  } = contact;

  const formattedDob = dateOfBirth
    ? DateTime.fromSQL(dateOfBirth).toFormat(DateFormat.longDate)
    : '';
  const [isLinkedAccountsModalVisible, setLinkedAccountsModalVisible] =
    useState<boolean>(false);

  const getAffiliationName = (name: string): string => {
    return name ? `${name}` : '';
  };

  const addressValue = [
    { value: getAffiliationName(address?.affiliationName) },
    { value: department },
    { value: address?.addressLine1 },
    { value: address?.addressLine2 },
    {
      value: `${address?.city ? address.city.concat(',') : ''} ${
        address?.state
      } ${address?.zip || ''}`,
    },
  ];

  const validAddressValue = addressValue.filter((addressItem) => {
    const value = addressItem.value;
    return value != null && value.trim() !== '' && value.trim() !== 'null';
  });

  const showResendInvitationView = isSupport && !hasSetPassword;

  const primaryDetails = getContactPrimaryDetails({
    chosenName,
    ethnicities,
    firstName,
    formattedDob,
    gender,
    lastName,
    middleName,
    prefix,
    pronouns,
    suffix,
  });

  const secondaryDetails = getContactSecondaryDetails({
    contactPreferences,
    email,
    faxNumber,
    homePhone,
    isProxy,
    isSupport,
    languages,
    officeNumber: officePhone,
    phoneNumber: cellPhone,
    relationToPatient,
    secondaryPhoneNumber: secondaryCellPhone,
  });

  const addressDetails = [
    {
      id: 'lives-with-patient',
      title: 'Lives with patient',
      value: isLivesWithPatient ? 'Yes' : 'No',
    },
    {
      id: 'main-address',
      isRequired: validAddressValue.length > 0,
      title: 'Main address',
      value: {
        fields: validAddressValue,
      },
    },
    {
      id: 'timezone',
      title: 'Timezone',
      value: timezone,
    },
  ];

  return (
    <>
      {isSupport && (
        <SupportLinkedAccountsModal
          isOpen={isLinkedAccountsModalVisible}
          linkedPatients={otherLinkedPatients}
          modalId={TOP_LEVEL_ID}
          onClose={() => setLinkedAccountsModalVisible(false)}
          supportEmail={email}
        />
      )}
      <ChakraSimpleGrid columns={3} marginTop="18px" spacing={10}>
        <ChakraGridItem>
          <ChakraFlex flexDirection="column" gap="18px" minHeight="300px">
            {primaryDetails.map(renderField)}
          </ChakraFlex>
        </ChakraGridItem>
        <ChakraGridItem>
          <ChakraFlex flexDirection="column" gap="18px" minHeight="300px">
            {secondaryDetails.map(renderField)}
          </ChakraFlex>
        </ChakraGridItem>
        <ChakraGridItem>
          <ChakraFlex flexDirection="column" gap="18px" minHeight="300px">
            {addressDetails.map(renderField)}
          </ChakraFlex>
        </ChakraGridItem>
      </ChakraSimpleGrid>
      {showResendInvitationView && (
        <ResendInvitation
          isSupport
          name={chosenName || firstName}
          userId={supportExternalId}
        />
      )}
    </>
  );
};

const PatientContactDetails = ({
  contact,
  onAdd,
  patientBasicDetails,
  patientId,
  supportEmailAddresses,
}: PatientContactProps) => {
  const { currentUser } = useGlobalContext();

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [openDeletePatientContactModal, setOpenDeletePatientContactModal] =
    useState(false);
  const [deletePatientContact, { isLoading: isDeleting }] =
    useDeletePatientContactMutation();

  const isSupport = useMemo<boolean>(() => contact.isSupport, [contact]);

  const onDeleteContactConfirm = async () => {
    try {
      await deletePatientContact({
        contactExternalId: contact.contactExternalId,
        patientId,
        userId: currentUser.userId,
      }).unwrap();
      setOpenDeletePatientContactModal(false);
      onAdd();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error deleting patient contact:', error);
    }
  };

  return (
    <>
      {isEditMode ? (
        <ChakraBox margin="24px 8px 0px 8px">
          <PatientAddContactV2
            contact={contact}
            onAdd={() => {
              onAdd();
              setIsEditMode(false);
            }}
            onDeletePatientSupportLink={() => setIsEditMode(false)}
            onDiscard={() => setIsEditMode(false)}
            patientBasicDetails={patientBasicDetails}
            patientId={patientId}
            supportEmailAddresses={supportEmailAddresses}
          />
        </ChakraBox>
      ) : (
        <ViewPatientContact contact={contact} />
      )}
      <DeleteConfirmModal
        buttonTitle="Delete contact"
        isOpen={openDeletePatientContactModal}
        isPrimaryButtonDisabled={isDeleting}
        onConfirmClick={onDeleteContactConfirm}
        setIsOpen={setOpenDeletePatientContactModal}
        subTitle="Are you sure you want to delete this contact? This action cannot be undone."
        title="Delete patient contact"
      />

      {!isEditMode && (
        <ChakraFlex marginTop="24px">
          {!isSupport && (
            <FireflyButton
              id="patient-contact__edit-icon"
              leftIcon={FAIconName.trashRegular}
              onClick={() => setOpenDeletePatientContactModal(true)}
              size="small"
              variant="secondary"
            >
              Delete
            </FireflyButton>
          )}

          <ChakraSpacer />
          <FireflyButton
            id="patient-contact__edit-icon"
            leftIcon={FAIconName.penRegular}
            onClick={() => setIsEditMode(true)}
            size="small"
            variant="secondary"
          >
            Edit
          </FireflyButton>
        </ChakraFlex>
      )}
    </>
  );
};

export default PatientContactDetails;
